/**
 * Klaviyo back-in-stock sign-up. Rendered inline in the desktop buy buttons (buy-buttons.liquid) and inside the
 * page-wide alert popover (product-alert-popup.liquid), which sold-out sizes and the mobile "Notify me…" button open.
 */
if (!customElements.get('klaviyo-backinstock')) {
    class KlaviyoBackInStock extends HTMLElement {
        static SELECTORS = {
            trigger: '.klaviyo-bis-trigger',
            email: 'input[type="email"]',
            resetEmail: '.backinstock-customer__email-close',
            customerEmailContainer: '.backinstock-customer__email-container',
            popover: 'x-popover',
            productForm: 'form[is="product-form"]',
            variantInput: 'input[name="id"]'
        };

        static CLASSES = {
            hidden: 'hidden',
            message: 'klaviyo-backinstock__message',
            messageSuccess: 'klaviyo-backinstock__message--success',
            messageError: 'klaviyo-backinstock__message--error',
            inputSuccess: 'input-success',
            inputError: 'input-error'
        };

        static EVENTS = {
            click: 'click',
            blur: 'blur',
            input: 'input',
            keydown: 'keydown',
            popoverBeforeShow: 'dialog:before-show'
        };

        static KEYS = { enter: 'Enter' };

        // One entry per message shown under the email field; stringKey points into window.themeVariables.klaviyo.strings
        static FEEDBACK = {
            success: {
                stringKey: 'success',
                role: 'status',
                inputClass: KlaviyoBackInStock.CLASSES.inputSuccess,
                messageClass: KlaviyoBackInStock.CLASSES.messageSuccess
            },
            invalidEmail: {
                stringKey: 'invalidEmail',
                role: 'alert',
                inputClass: KlaviyoBackInStock.CLASSES.inputError,
                messageClass: KlaviyoBackInStock.CLASSES.messageError
            },
            error: {
                stringKey: 'error',
                role: 'alert',
                inputClass: KlaviyoBackInStock.CLASSES.inputError,
                messageClass: KlaviyoBackInStock.CLASSES.messageError
            }
        };

        static API = {
            url: 'https://a.klaviyo.com/client/back-in-stock-subscriptions',
            revision: '2024-06-15',
            method: 'POST',
            contentType: 'application/json',
            subscriptionType: 'back-in-stock-subscription',
            profileType: 'profile',
            variantType: 'catalog-variant',
            variantIdPrefix: '$shopify:::$default:::',
            channels: ['EMAIL']
        };

        static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Long enough to read the confirmation before the popover closes itself
        static POPOVER_CLOSE_DELAY_MS = 4000;

        // The popover hosting this moves itself to the body when it opens, which disconnects and reconnects this
        // element: every connect rebuilds the state from the DOM and the AbortController drops the previous listeners,
        // otherwise each open would add another click handler and send the subscription once more.
        connectedCallback() {
            this._abort?.abort();
            this._abort = new AbortController();

            this._trigger = this.querySelector(KlaviyoBackInStock.SELECTORS.trigger);
            this._email = this.querySelector(KlaviyoBackInStock.SELECTORS.email);
            this._resetEmail = this.querySelector(KlaviyoBackInStock.SELECTORS.resetEmail);
            this._popover = this.closest(KlaviyoBackInStock.SELECTORS.popover);

            const klaviyo = window.themeVariables?.klaviyo || {};
            this._publicApiKey = klaviyo.publicApiKey || null;
            this._strings = klaviyo.strings || {};

            if (!this._trigger || !this._email) return;

            this._bindEvents();
        }

        disconnectedCallback() {
            this._abort?.abort();
        }

        _bindEvents() {
            const { signal } = this._abort;
            const { EVENTS } = KlaviyoBackInStock;

            this._trigger.addEventListener(EVENTS.click, (event) => this._onSubmit(event), { signal });
            this._email.addEventListener(EVENTS.keydown, (event) => this._onEmailKeydown(event), { signal });
            this._email.addEventListener(EVENTS.blur, () => this._onEmailBlur(), { signal });
            this._email.addEventListener(EVENTS.input, () => this._clearFeedback(), { signal });
            this._resetEmail?.addEventListener(EVENTS.click, () => this._showEmailInput(), { signal });

            // The popover is shared by the whole page, so only its opener knows which size the customer asked about:
            // sold-out sizes (option-value.liquid) and the mobile "Notify me…" button name their variant in data-id.
            this._popover?.addEventListener(EVENTS.popoverBeforeShow, (event) => this._onPopoverShow(event), { signal });
        }

        async _onSubmit(event) {
            event.preventDefault();

            if (this._isSubmitting) return;

            if (!this._isValidEmail()) {
                this._showFeedback(KlaviyoBackInStock.FEEDBACK.invalidEmail);
                return;
            }

            const variantId = this._getVariantId();

            if (!variantId || !this._publicApiKey) {
                this._showFeedback(KlaviyoBackInStock.FEEDBACK.error);
                return;
            }

            this._isSubmitting = true;

            try {
                const response = await fetch(this._buildUrl(), this._buildRequest(variantId));
                if (!response.ok) throw new Error(`Klaviyo back-in-stock subscription failed (${response.status})`);

                this._showFeedback(KlaviyoBackInStock.FEEDBACK.success);
                this._closePopoverSoon();
            } catch (error) {
                console.error(error);
                this._showFeedback(KlaviyoBackInStock.FEEDBACK.error);
            } finally {
                this._isSubmitting = false;
            }
        }

        // On desktop the field sits inside the product form (buy-buttons.liquid) and the trigger is a link, so Enter
        // would otherwise be the form's implicit submission — an add to cart of a sold-out variant
        _onEmailKeydown(event) {
            if (event.key !== KlaviyoBackInStock.KEYS.enter) return;

            this._onSubmit(event);
        }

        _onEmailBlur() {
            // An untouched field is not an error yet, the submit will flag it
            if (!this._getEmail() || this._isValidEmail()) return;

            this._showFeedback(KlaviyoBackInStock.FEEDBACK.invalidEmail);
        }

        _onPopoverShow(event) {
            clearTimeout(this._closeTimer);
            this._variantId = event.detail?.activator?.dataset.id || null;
            this._clearFeedback();
        }

        _getEmail() {
            return this._email.value.trim();
        }

        _isValidEmail() {
            return KlaviyoBackInStock.EMAIL_PATTERN.test(this._getEmail());
        }

        // Falls back to the variant the product form currently holds, for any opener that names none
        _getVariantId() {
            if (this._variantId) return this._variantId;

            const { productForm, variantInput } = KlaviyoBackInStock.SELECTORS;
            const form = this.closest(productForm) || document.querySelector(productForm);

            return form?.querySelector(variantInput)?.value || null;
        }

        _buildUrl() {
            return `${KlaviyoBackInStock.API.url}?company_id=${encodeURIComponent(this._publicApiKey)}`;
        }

        _buildRequest(variantId) {
            const { API } = KlaviyoBackInStock;

            return {
                method: API.method,
                headers: { 'Content-Type': API.contentType, revision: API.revision },
                body: JSON.stringify(this._buildPayload(variantId))
            };
        }

        _buildPayload(variantId) {
            const { API } = KlaviyoBackInStock;

            return {
                data: {
                    type: API.subscriptionType,
                    attributes: {
                        profile: {
                            data: { type: API.profileType, attributes: { email: this._getEmail() } }
                        },
                        channels: API.channels
                    },
                    relationships: {
                        variant: {
                            data: { type: API.variantType, id: `${API.variantIdPrefix}${variantId}` }
                        }
                    }
                }
            };
        }

        _showFeedback(feedback) {
            this._clearFeedback();

            this._email.classList.add(feedback.inputClass);

            this._message = document.createElement('p');
            this._message.classList.add(KlaviyoBackInStock.CLASSES.message, feedback.messageClass);
            this._message.setAttribute('role', feedback.role);
            this._message.textContent = this._decodeEntities(this._strings[feedback.stringKey]);
            this._trigger.before(this._message);
        }

        // Liquid's `t` filter HTML-escapes the strings (an apostrophe arrives as &#39;), so decode before using as text
        _decodeEntities(html) {
            if (!html) return '';

            const textarea = document.createElement('textarea');
            textarea.innerHTML = html;

            return textarea.value;
        }

        _clearFeedback() {
            const { inputSuccess, inputError } = KlaviyoBackInStock.CLASSES;

            this._email.classList.remove(inputSuccess, inputError);
            this._message?.remove();
            this._message = null;
        }

        // Not cleared on disconnect: the popover reparents itself (and so disconnects us) while this is pending
        _closePopoverSoon() {
            if (!this._popover) return;

            clearTimeout(this._closeTimer);
            this._closeTimer = setTimeout(() => this._popover.hide(), KlaviyoBackInStock.POPOVER_CLOSE_DELAY_MS);
        }

        // Logged-in customers see their email as text; the close icon swaps it for an empty, editable field
        _showEmailInput() {
            const container = this._resetEmail.closest(KlaviyoBackInStock.SELECTORS.customerEmailContainer);

            Array.from(container.children).forEach((child) => child.classList.toggle(KlaviyoBackInStock.CLASSES.hidden));
            this._email.value = '';
            this._email.focus();
        }
    }

    customElements.define('klaviyo-backinstock', KlaviyoBackInStock);
}
